'use client';

import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { useActionState, useEffect, useMemo, useState } from 'react';

import { requestPasswordReset } from '@/lib/auth/actions';
import { twMerge } from '@/tailwind-merge.config';

interface ForgotPasswordFormProps {
  timingToken: string;
}

export function ForgotPasswordForm({ timingToken }: ForgotPasswordFormProps) {
  const { t } = useTranslation('common');
  const [errorMessage, formAction, isPending] = useActionState(
    requestPasswordReset,
    undefined
  );
  const [showSuccess, setShowSuccess] = useState(false);
  const [email, setEmail] = useState('');

  // Client-side validation
  const isValid = useMemo(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return email.length > 0 && emailRegex.test(email);
  }, [email]);

  useEffect(() => {
    if (errorMessage === 'success') {
      setShowSuccess(true);
      setEmail('');
    }
  }, [errorMessage]);

  const inputStyles = twMerge(
    'block h-[40px] w-full border border-black bg-white px-4 py-2 text-p1CopyDesktop font-light',
    'placeholder:text-black',
    'focus:border-black focus:outline-none focus:ring-0',
    'disabled:cursor-not-allowed disabled:bg-gray-100'
  );

  if (showSuccess) {
    return (
      <div className="w-full bg-white">
        <div className="mx-auto max-w-2xl px-8 py-10">
          <div className="rounded-lg bg-green-50 p-6">
            <div className="flex items-start">
              <div className="shrink-0">
                <svg
                  className="size-6 text-green-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="mb-2 text-lg font-medium text-green-800">
                  {t('auth.forgot-password-page.success-title')}
                </h3>
                <p className="text-sm text-green-700">
                  {t('auth.forgot-password-page.success-message')}
                </p>
                <div className="mt-4">
                  <Link
                    href="/user/login"
                    className="btn btn--primary btn--icon btn--icon--arrow-left w-fit shrink-0 uppercase"
                  >
                    {t('auth.forgot-password-page.back-to-login')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white">
      <div className="mx-auto max-w-2xl px-8 py-10">
        <div className="mb-8">
          <h1 className="mb-2 text-h1Mobile uppercase tablet:text-h1Desktop">
            {t('auth.forgot-password-page.title')}
          </h1>
          <p className="text-p1CopyDesktop font-light text-gray-600">
            {t('auth.forgot-password-page.subtitle')}
          </p>
        </div>

        <form action={formAction} className="space-y-6">
          {/* Honeypot-Feld für Spam-Schutz (unsichtbar für echte User) */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-[-9999px] top-[-9999px] opacity-0"
          >
            <label htmlFor="website">Website (leave empty)</label>
            <input
              type="text"
              id="website"
              name="website"
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {/* Timing-Token für Spam-Schutz */}
          <input type="hidden" name="_timing" value={timingToken} />

          {/* Email Field */}
          <div>
            <label
              htmlFor="email"
              className="mb-2 block text-sm font-normal uppercase leading-normal"
            >
              {t('auth.forgot-password-page.email-label')} *
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              disabled={isPending}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputStyles}
            />
          </div>

          {/* Error Message */}
          {errorMessage && errorMessage !== 'success' && (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">
                {t(`auth.errors.${errorMessage}`)}
              </p>
            </div>
          )}

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={isPending || !isValid}
              className={twMerge(
                'btn btn--primary btn--icon btn--icon--arrow-left w-fit shrink-0 uppercase',
                (!isValid || isPending) && 'cursor-not-allowed opacity-50'
              )}
            >
              {isPending
                ? t('auth.forgot-password-page.submitting')
                : t('auth.forgot-password-page.submit')}
            </button>
          </div>

          {/* Back to Login Link */}
          <div className="border-t border-gray-200 pt-4">
            <p className="text-sm text-gray-600">
              <Link
                href="/user/login"
                className="font-medium text-black underline hover:no-underline"
              >
                {t('auth.forgot-password-page.back-to-login')}
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
